import React from 'react';
import { Drawer, Box, Typography } from '@mui/material';
import { Link, useLocation, useSearchParams } from 'react-router-dom';

// Feather-style stroked icons (match prototype)
import DashboardCustomizeOutlinedIcon from '@mui/icons-material/DashboardCustomizeOutlined';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import FavoriteBorderOutlinedIcon from '@mui/icons-material/FavoriteBorderOutlined';

import { drawerWidth } from '../constants/layout';
import { useAuth } from '../auth/AuthProvider';
import { colors, typography, gradient } from '../theme/designTheme';

// ── Nav config ────────────────────────────────────────────────────────

const mainNav = [
  { text: 'Dashboard', Icon: DashboardCustomizeOutlinedIcon, path: '/' },
  { text: 'Cases',     Icon: FolderOpenOutlinedIcon,         path: '/cases' },
  { text: 'Documents', Icon: DescriptionOutlinedIcon,        path: '/documents' },
  { text: 'Reports',   Icon: ChatBubbleOutlineOutlinedIcon,  path: '/reports' },
];

const systemNav = [
  { text: 'Admin', Icon: PeopleAltOutlinedIcon, path: '/admin' },
];

const toolsNav = [
  { text: 'Settings', Icon: SettingsOutlinedIcon,   path: '/settings' },
  { text: 'Help',     Icon: HelpOutlineOutlinedIcon, path: '/help' },
];

// ── Shared chrome pieces ──────────────────────────────────────────────

const Brand = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1.25, pt: 0.5, pb: 3.5 }}>
    <Box sx={{
      width: 32, height: 32, borderRadius: '8px',
      background: gradient.primary,
      display: 'grid', placeItems: 'center',
      color: '#fff',
      fontFamily: typography.fontFamilyDisplay,
      fontWeight: 800, fontSize: '14px',
      letterSpacing: '0.02em',
      boxShadow: '0 4px 12px rgba(0, 90, 196, 0.25)',
    }}>
      L
    </Box>
    <Typography sx={{
      fontFamily: typography.fontFamilyDisplay,
      fontWeight: 800, fontSize: '20px',
      letterSpacing: '-0.02em',
      background: gradient.primary,
      WebkitBackgroundClip: 'text', backgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      userSelect: 'none',
      lineHeight: 1,
    }}>
      LISA
    </Typography>
  </Box>
);

const GroupLabel = ({ children }) => (
  <Typography sx={{
    fontFamily: typography.fontFamily,
    fontSize: '10px',
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: colors.outlineVariant,
    fontWeight: 600,
    px: 1.5, pt: 2, pb: 0.75,
  }}>
    {children}
  </Typography>
);

const NavLink = ({ text, Icon, path, isActive, onClick, danger, disabled }) => {
  const activeBg = isActive ? colors.primaryContainer : 'transparent';
  const activeColor = isActive ? colors.onPrimaryContainer : colors.textSecondary;
  return (
    <Box
      component={onClick || disabled ? 'div' : Link}
      to={onClick || disabled ? undefined : path}
      onClick={disabled ? undefined : onClick}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        px: 1.5, py: 1.1, mx: 0, borderRadius: '8px',
        fontSize: '13px', fontWeight: isActive ? 600 : 500,
        color: danger ? colors.critical : activeColor,
        bgcolor: activeBg,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        textDecoration: 'none',
        transition: 'background 0.15s, color 0.15s',
        fontFamily: typography.fontFamily,
        '&:hover': disabled ? {} : {
          bgcolor: isActive ? colors.primaryContainer : colors.canvas,
          color: danger ? colors.critical : (isActive ? colors.onPrimaryContainer : colors.textPrimary),
        },
      }}
    >
      <Icon sx={{ fontSize: 17, strokeWidth: 2, flexShrink: 0 }} />
      <Box component="span" sx={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {text}
      </Box>
    </Box>
  );
};

const UserChip = () => {
  const { user, displayRole } = useAuth();
  const userName = user?.name || 'Dev User';
  const initials = user?.initials || 'DU';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1.25, py: 1, borderRadius: '8px' }}>
      <Box sx={{
        width: 32, height: 32, borderRadius: '50%',
        background: gradient.primary,
        color: '#fff',
        display: 'grid', placeItems: 'center',
        fontFamily: typography.fontFamilyDisplay, fontWeight: 700, fontSize: '12px',
        flexShrink: 0,
      }}>
        {initials}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{
          fontFamily: typography.fontFamily, fontSize: '13px', fontWeight: 600,
          color: colors.textPrimary, lineHeight: 1.2,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {userName}
        </Typography>
        <Typography sx={{
          fontFamily: typography.fontFamily, fontSize: '11px',
          color: colors.textSecondary,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {displayRole || 'Analyst'}
        </Typography>
      </Box>
    </Box>
  );
};

// ── Regular sidebar ───────────────────────────────────────────────────

const RegularSidebar = ({ isActive }) => {
  const { hasRole, logout } = useAuth();
  const showAdmin = hasRole ? hasRole('Admin') : true;

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', height: '100%',
      p: '24px 16px 20px', gap: 0.5,
    }}>
      <Brand />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        {mainNav.map((item) => (
          <NavLink key={item.text} {...item} isActive={isActive(item.path)} />
        ))}
      </Box>

      {showAdmin && (
        <>
          <GroupLabel>System</GroupLabel>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {systemNav.map((item) => (
              <NavLink key={item.text} {...item} isActive={isActive(item.path)} />
            ))}
          </Box>
        </>
      )}

      <GroupLabel>Tools</GroupLabel>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        {toolsNav.map((item) => (
          <NavLink key={item.text} {...item} isActive={isActive(item.path)} />
        ))}
      </Box>

      <Box sx={{ flex: 1 }} />

      {/* Footer: logout + user chip */}
      <Box sx={{
        pt: 1.75, borderTop: `1px solid ${colors.canvas}`,
        display: 'flex', flexDirection: 'column', gap: 0.5,
      }}>
        <NavLink text="Log out" Icon={LogoutOutlinedIcon} onClick={logout} />
        <UserChip />
      </Box>
    </Box>
  );
};

// ── Admin sidebar (preserved) ─────────────────────────────────────────

const AdminSidebar = () => {
  const [searchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'feedback';
  const { logout } = useAuth();

  const adminNav = [
    { text: 'Feedback Review',    Icon: AssignmentOutlinedIcon,     path: '/admin?tab=feedback', tabKey: 'feedback' },
    { text: 'Agent Performance',  Icon: BarChartOutlinedIcon,       path: '/admin?tab=agents',   tabKey: 'agents' },
    { text: 'System Health',      Icon: FavoriteBorderOutlinedIcon, path: '/admin?tab=health',   tabKey: 'health', disabled: true },
    { text: 'User Management',    Icon: PeopleAltOutlinedIcon,      path: '/admin?tab=users',    tabKey: 'users',  disabled: true },
  ];

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', height: '100%',
      p: '24px 16px 20px', gap: 0.5,
    }}>
      <Brand />

      <NavLink text="Back" Icon={ArrowBackOutlinedIcon} path="/" />

      <GroupLabel>Administration</GroupLabel>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        {adminNav.map((item) => (
          <NavLink
            key={item.tabKey}
            text={item.text}
            Icon={item.Icon}
            path={item.path}
            isActive={tab === item.tabKey}
            disabled={item.disabled}
          />
        ))}
      </Box>

      <Box sx={{ flex: 1 }} />

      <Box sx={{
        pt: 1.75, borderTop: `1px solid ${colors.canvas}`,
        display: 'flex', flexDirection: 'column', gap: 0.5,
      }}>
        <NavLink text="Log out" Icon={LogoutOutlinedIcon} onClick={logout} />
        <UserChip />
      </Box>
    </Box>
  );
};

// ── Main ──────────────────────────────────────────────────────────────

const Sidebar = () => {
  const location = useLocation();
  const isAdminMode = location.pathname.startsWith('/admin');
  const isActive = (path) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <Drawer
      variant="permanent"
      anchor="left"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: `${drawerWidth}px`,
          boxSizing: 'border-box',
          backgroundColor: colors.surface,
          borderRight: 'none',
          boxShadow: '1px 0 0 rgba(43,52,56,0.04)',
          position: 'fixed',
          top: 0, left: 0,
          height: '100vh',
          overflowY: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {isAdminMode ? <AdminSidebar /> : <RegularSidebar isActive={isActive} />}
    </Drawer>
  );
};

export default Sidebar;
